import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
  ValidatorFn,
  AbstractControl,
} from '@angular/forms';
import { translate, translateObject } from '@jsverse/transloco';

import { MarkdownModule } from 'ngx-markdown';

import { Subject, takeUntil } from 'rxjs';

import { v4 as UUIDv4 } from 'uuid';

import { AppSharedModule } from 'app/app-shared.module';
import { SimpleDialogButtonColor } from 'app/components/simple-dialog.component';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { Comment, CommentTarget, CommentType } from 'app/providers/bridge-api/dashboard.service';
import { SemantriaUsersService } from 'app/providers/semantria-api/semantria-users.service';
import { DialogService } from 'app/services/dialog.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { Utils } from 'app/utils/utils';

import { CommentDataChange } from '../../services/chart-report/chart-report-abstract/chart-report-abstract';
import { WidgetCommentsHandlerService } from '../../services/widget-comments-handler.service';

export interface CommentDialogOptions {
  comment?: Comment;
  initialContent?: {
    title?: string;
    summary?: string;
    details?: string;
  };
  type?: CommentType;
  smartSummaryId?: string;
  smartSummaryExists: boolean;
  commentChanges?: Map<string, CommentDataChange>;
  targetPoint: CommentTarget;
  isCommentVisible: boolean;
  isReadOnly: boolean;
  isDashboardLocked: boolean;
  isPinningDisabled: boolean;
}

export type CommentMode = 'CREATE' | 'EDIT' | 'VIEW' | 'DELETE';

@EnableClassTelemetry()
@Component({
  selector: 'app-comment-dialog',
  imports: [AppSharedModule, MarkdownModule],
  templateUrl: './comment-dialog.component.html',
  styleUrls: ['./comment-dialog.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CommentDialogComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() options: CommentDialogOptions;

  @Output() modalClosed: EventEmitter<{ action: CommentMode; comment: Comment }> =
    new EventEmitter<{ action: CommentMode; comment: Comment }>();
  @Output() openSmartSummary = new EventEmitter<{ pointName: string; smartSummaryId: string }>();

  private dialogService = inject(DialogService);
  private authService = inject(AuthService);
  private formBuilder = inject(FormBuilder);
  private userService = inject(SemantriaUsersService);
  commentsHandlerService: WidgetCommentsHandlerService = inject(WidgetCommentsHandlerService);

  mode: CommentMode = 'CREATE';
  comment: Comment;
  commentCreator = '\u2026';

  dataChangeText;

  commentForm: FormGroup;
  saveButtonTooltip: { text: string };
  hasChanges = signal(true);
  isSaving = signal(false);
  isValid = signal(false);
  isSaveDisabled = computed(() => this.isSaving() || !this.isValid() || !this.hasChanges());

  unsub$ = new Subject<void>();
  pinTooltip;

  oneFieldRequired: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const title = control.get('title').value;
    const summary = control.get('summary').value;
    const details = control.get('details').value;
    return !title && !summary && !details ? { required: true } : null;
  };

  constructor() {
    this.commentForm = this.formBuilder.group(
      {
        title: ['', Validators.maxLength(40)],
        titlePinned: false,
        summary: ['', Validators.maxLength(300)],
        summaryPinned: false,
        details: '',
      },
      { validators: this.oneFieldRequired }
    );
  }

  ngOnInit(): void {
    if (this.options.isReadOnly || this.options.isDashboardLocked) {
      this.commentForm.disable();
    }

    this.commentForm.statusChanges.pipe(takeUntil(this.unsub$)).subscribe((status) => {
      this.saveButtonTooltip = null;
      if (status === 'INVALID' && this.commentForm.errors) {
        this.saveButtonTooltip = { text: translate('widgets.comments.one-field-required') };
      }
    });

    this.commentForm.valueChanges.pipe(takeUntil(this.unsub$)).subscribe((formValue) => {
      if (!formValue.title && formValue.titlePinned) {
        this.commentForm.controls.titlePinned.setValue(false, { emitEvent: false });
      }

      if (!formValue.summary && formValue.summaryPinned) {
        this.commentForm.controls.summaryPinned.setValue(false, { emitEvent: false });
      }

      this.isValid.set(this.commentForm.valid);
    });

    this.comment = this.options.comment;
    this.saveButtonTooltip = { text: translate('widgets.comments.one-field-required') };

    if (this.comment) {
      this.mode = 'EDIT';
      this.hasChanges.set(false);
      this.saveButtonTooltip = null;

      this.dataChangeText = this.assembleDataChangeText();

      this.userService
        .getDisplayNameForUser(this.comment.createdBy)
        .then((res) => (this.commentCreator = res));

      this.commentForm.controls.title.setValue(this.comment.title);
      this.commentForm.controls.titlePinned.setValue(!!this.comment.titlePinned);
      this.commentForm.controls.summary.setValue(this.comment.summary);
      this.commentForm.controls.summaryPinned.setValue(!!this.comment.summaryPinned);
      this.commentForm.controls.details.setValue(this.comment.content);

      this.commentForm.valueChanges.pipe(takeUntil(this.unsub$)).subscribe((formValue) => {
        this.hasChanges.set(
          formValue.title !== this.comment.title ||
            formValue.titlePinned !== this.comment.titlePinned ||
            formValue.summary !== this.comment.summary ||
            formValue.summaryPinned !== this.comment.summaryPinned ||
            formValue.details !== this.comment.content
        );
      });
    }

    if (this.options.initialContent) {
      this.commentForm.controls.title.setValue(this.options.initialContent.title ?? '');
      this.commentForm.controls.summary.setValue(this.options.initialContent.summary ?? '');
      this.commentForm.controls.details.setValue(this.options.initialContent.details ?? '');
    }

    if (this.options.isPinningDisabled && !this.options.isReadOnly) {
      this.pinTooltip = translate('widgets.comments.pinning-focus-areas-disabled');
    }
  }

  ngOnDestroy(): void {
    this.unsub$.next();
    this.unsub$.complete();
  }

  private assembleDataChangeText() {
    let dataChangeText;

    const commentChange = this.options.commentChanges?.get(this.comment.id);
    if (commentChange) {
      const changedProp = commentChange.y !== undefined ? 'y' : 'x';

      dataChangeText = translate('widgets.comments.list.change-reason.data-changed-from', {
        oldValue: Utils.round(this.comment.target.point[changedProp], 2),
      });
      if (!isNaN(commentChange[changedProp])) {
        dataChangeText += translate('widgets.comments.list.change-reason.data-changed-to', {
          newValue: Utils.round(commentChange[changedProp], 2),
        });
      }
      if (commentChange.reasons.length === 1 && commentChange.reasons[0] !== 'UNKNOWN') {
        dataChangeText += translate(
          `widgets.comments.list.change-reason.due-to.${commentChange.reasons[0]}`
        );
      }
    }

    return dataChangeText;
  }

  canPin(controlName) {
    return (
      !this.options.isPinningDisabled &&
      !this.options.isReadOnly &&
      !this.options.isDashboardLocked &&
      !!this.commentForm.controls[controlName].value
    );
  }

  isPinned(controlName) {
    return this.commentForm.controls[`${controlName}Pinned`].value;
  }

  togglePinned(controlName) {
    this.commentForm.controls[`${controlName}Pinned`].setValue(
      !this.commentForm.controls[`${controlName}Pinned`].value
    );
  }

  onSmartSummaryClicked() {
    this.openSmartSummary.emit({
      pointName: this.comment.target.analyticItems[0].name,
      smartSummaryId: this.comment.smartSummaryId,
    });
  }

  onCancel() {
    this.modalClosed.emit();
  }

  async onSave() {
    if (!this.isValid() || !this.hasChanges()) {
      return;
    }

    if (this.mode === 'CREATE') {
      await this.onCreate();
      return;
    }

    this.onUpdate();
  }

  private async onCreate() {
    this.isSaving.set(true);

    const session = await this.authService.getSession();

    this.comment = {
      id: UUIDv4(),
      title: this.commentForm.controls.title.value,
      titlePinned: this.commentForm.controls.titlePinned.value,
      summary: this.commentForm.controls.summary.value,
      summaryPinned: this.commentForm.controls.summaryPinned.value,
      content: this.commentForm.controls.details.value,
      type: this.options.type ?? 'custom',
      target: this.options.targetPoint,
      createdBy: session.info2.user_id,
      alwaysVisible: false,
      createdAt: new Date(),
    };

    if (this.options.smartSummaryId) {
      this.comment.smartSummaryId = this.options.smartSummaryId;
    }

    this.modalClosed.emit({ action: this.mode, comment: this.comment });
  }

  private onUpdate() {
    this.comment.title = this.commentForm.controls.title.value;
    this.comment.titlePinned = this.commentForm.controls.titlePinned.value;
    this.comment.summary = this.commentForm.controls.summary.value;
    this.comment.summaryPinned = this.commentForm.controls.summaryPinned.value;
    this.comment.content = this.commentForm.controls.details.value;
    this.comment.modifiedAt = new Date();

    const commentChange = this.options.commentChanges.get(this.comment.id);
    if (commentChange?.y) {
      this.comment.target.point.y = commentChange.y;
    }
    if (commentChange?.x) {
      this.comment.target.point.x = commentChange.x;
    }

    this.modalClosed.emit({ action: this.mode, comment: this.comment });
  }

  onDelete() {
    const translations = translateObject('widgets.comments.delete-dialog');
    this.dialogService
      .showSimpleDialog({
        title: translations['title'],
        content: translations['content'],
        confirmTitle: translations['confirm'],
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.modalClosed.emit({ action: 'DELETE', comment: this.comment });
        }
      });
  }
}
